import { forwardRef, useEffect, useMemo, useState } from "react";

import { useParams, useSearchParams } from "@monta/router-react";
import { useSelectedStudentIdContext } from "#/components/exam-results/context/studentsList/context";
import GeneralInfoTable from "#/components/exam-results/GeneralInfoTable";
import Infographic from "#/components/exam-results/infographic";
import Loading from "#/components/loading";
import {
  getExamSummaryInfo,
  getMainResultSet
} from "#/services/rest/gwt/exam-results/views/type-1/multi-user.api";
import GeneralInfoHeader from "./GeneralInfoHeader";
import MultiLesson from "./MultiLesson";
import styles from "./styles.module.scss";

import type { MainResultSet } from "#/services/rest/gwt/exam-results/views/type-1/multi-user.types";

const GeneralInfo = forwardRef<HTMLDivElement, object>((_, contentRef) => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const examTakingId = Number(params["examTakingId"]);
  const { selectedStudentId } = useSelectedStudentIdContext();
  const isSimulator = searchParams.get("isSimulator");

  const [isSingleLesson, setIsSingleLesson] = useState(false);
  const [bookData, setBookData] = useState<MainResultSet[]>([]);
  const [topicData, setTopicData] = useState<MainResultSet[]>([]);
  const [overallInfo, setOverallInfo] = useState<MainResultSet>();

  const { data, isLoading, mutate } = getMainResultSet.useMutation({
    onSuccess: (res) => {
      const overallInfo = res.find((item) => item.id === null);
      setOverallInfo(overallInfo);
      const actualData = res.filter((item) => item.orderInExam !== null);
      setBookData(actualData.filter((item) => item.isModule));
      setTopicData(
        actualData.filter((item) => !item.isModule && item.id !== null)
      );
    }
  });

  const { data: examInfo, isLoading: examInfoLoading } =
    getExamSummaryInfo.useQuery({
      request: { body: { examTakingDetailId: examTakingId } }
    });

  useEffect(() => {
    if (bookData.length === 1 && topicData.length === 1) {
      setIsSingleLesson(true);
    }
  }, [bookData, topicData]);

  useEffect(() => {
    if (selectedStudentId) {
      mutate({
        body: {
          examTakingDetailId: examTakingId,
          studentId: selectedStudentId
        }
      });
    }
  }, [examTakingId, selectedStudentId]);

  const totalQuestionCount = useMemo(() => {
    if (isSingleLesson && overallInfo) {
      return (
        overallInfo.correctAnswers +
        overallInfo.noAnswers +
        overallInfo.incorrectAnswers
      );
    }
  }, [overallInfo, isSingleLesson]);

  const studentInfo = localStorage.getItem("currentStudentInfo");

  const mainContent = (() => {
    if (data && isSimulator) {
      return (
        <GeneralInfoTable
          data={data}
          totalStudentNO={examInfo?.participantNo ?? 0}
        />
      );
    } else if (overallInfo && examInfo && isSingleLesson) {
      return (
        <Infographic
          correctCount={overallInfo.correctAnswers}
          examName={examInfo.examName}
          finalMark={overallInfo.mark}
          wrongCount={overallInfo.incorrectAnswers}
          uncheckedCount={overallInfo.noAnswers}
          totalCount={totalQuestionCount ?? 0}
          date={overallInfo.takingDate ?? ""}
        />
      );
    } else if (examInfo) {
      return (
        <MultiLesson
          overallInfo={examInfo}
          bookData={bookData}
          topicData={topicData}
        />
      );
    }
    return null;
  })();

  return (
    <>
      {isLoading || examInfoLoading ? (
        <Loading />
      ) : (
        <div ref={contentRef} className={styles["print"]}>
          <div className={styles["print__header"]}>
            کارنامه دانش آموز، {studentInfo}
          </div>

          {examInfo && overallInfo && (
            <GeneralInfoHeader
              takingDate={examInfo.startTakingDateTime}
              name={examInfo.examName}
              rank={overallInfo.rank}
              level={overallInfo.level}
              totalStudentNO={examInfo.participantNo}
              presentStudentNo={examInfo.presentParticipantNo}
              tierNO={overallInfo.tierNO}
              growthStatus={overallInfo.growthStatus}
              gradeId={examInfo.grade}
              majorId={examInfo.majorId}
            />
          )}

          {isSimulator || isSingleLesson ? (
            <div className={styles["subtitle"]}>بررسی درس به درس</div>
          ) : null}
          {mainContent}
        </div>
      )}
    </>
  );
});

GeneralInfo.displayName = "GeneralInfo";

export default GeneralInfo;
